// External Dependencies:
import React from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { DateTime } from 'luxon'

// Internal Dependencies:
import NowPlayingButton from './NowPlayingButton'
import TrackBar from './TrackBar'
import MiniSchedule from 'components/Schedule/MiniSchedule'
import SiteNotice from 'components/SiteNotice/SiteNotice'
import { setUrlParameter } from 'utilities/url'

// Redux:
import { goToPreviousTrack, goToNextTrack, rewind, fastForward, setSelectedItem, setSeeking, calculateSeekPercent } from 'actions/schedule'
import { likeProgram, showProgramMenu } from 'actions/site'

class NowPlaying extends React.Component {
	constructor( props ) {
		super( props )

		this.state = {
			clickedLike: false,
			clickedDislike: false,
			showLikeNotice: false,
			showFirstLikeNotice: false
		}
	}

	handlePreviousClick = () => {
		const { goToPreviousTrack } = this.props

		goToPreviousTrack()
	}

	handleNextClick = () => {
		const { goToNextTrack, canGoToNext } = this.props

		if ( canGoToNext ) {
			goToNextTrack()
		}
	}

	handleRewindClick = () => {
		this.props.rewind()
	}

	handleFastForwardClick = () => {
		const { fastForward, setSeeking, calculateSeekPercent, canFastForward } = this.props

		if ( canFastForward ) {
			setSeeking( true )

			setTimeout( () => {
				fastForward()
			}, 100 )

			setTimeout( () => {
				setSeeking( false )
				calculateSeekPercent()
			}, 3000 )
		}
	}

	handleLiveClick = () => {
		this.props.goToLive()
	}

	handleItemClick = () => {
		this.props.setSelectedItem( this.props.nowPlaying.key )
	}

	handleTextClick = () => {
		const { showProgramMenu, nowPlaying, openLinksInTab, programsById, isEmbedded } = this.props

		if ( ! nowPlaying ) {
			return
		}

		if ( openLinksInTab ) {
			let url = siteData.site_url,
				programExtraData

			if ( programsById[ `id_${nowPlaying.series_slug}` ] ) {
				programExtraData = programsById[ `id_${nowPlaying.series_slug}` ]
			}

			url = programExtraData ? `/programs/${ programExtraData.slug }` : url

			if ( isEmbedded ) {
				url = setUrlParameter( url, 'utm_source', 'embed' )
			}

			window.open( url, '_blank' )

			return
		}

		showProgramMenu( nowPlaying )
	}

	handleLikeClick = () => {
		const { loggedIn, likeProgram, nowPlaying, accountData } = this.props

		if ( ! loggedIn ) {
			this.setState( {
				showLikeNotice: true
			} )

			return
		}

		if ( ! nowPlaying || ! nowPlaying.audio_file_url ) {
			return
		}

		if ( accountData && ! accountData.likes || ! Object.keys( accountData.likes ).length ) {
			this.setState( {
				showFirstLikeNotice: true
			} )
		}

		likeProgram( nowPlaying )

		this.setState( {
			clickedLike: true
		} )

		setTimeout( () => {
			this.setState( {
				clickedLike: false
			} )
		}, 700 )
	}

	handleCloseLikeNotice = () => {
		this.setState( {
			showLikeNotice: false,
			showFirstLikeNotice: false
		} )
	}

	handleShareClick = () => {
		const { showProgramMenu, nowPlaying } = this.props

		if ( ! nowPlaying ) {
			return
		}

		showProgramMenu( nowPlaying, 'share' )
	}

	render() {
		const {
			isBuffering, isLive, nowPlaying, isPlaying, mediaError, canFastForward, canGoToNext, showRewindWarning, rewindMaxHours, accountData,
			showMiniSchedule = true,
			showLikeButtons = true
		} = this.props
		const { clickedLike, clickedDislike, showLikeNotice, showFirstLikeNotice } = this.state
		let wrapClassName = 'now-playing-wrap'
		let rewindClass = '-rewind'
		let fastForwardClass = canFastForward ? '-fast-forward' : '-fast-forward is-disabled'
		const nextClass = canGoToNext ? '-next icon-angle-double-right-light' : '-next icon-angle-double-right-light is-disabled'
		let isLiveStream = nowPlaying.is_livestream
		let playStateText = 'Now Playing:'
		const url = nowPlaying.is_program ? `/schedule-item/${nowPlaying.key}` : '/schedule/'

		if ( isLiveStream && isLive ) {
			rewindClass += ' is-disabled'
			fastForwardClass += ' is-disabled'
		}

		let showLoading = false

		if ( isBuffering && isPlaying && ! showRewindWarning ) {
			showLoading = true
			wrapClassName += ' is-loading'

			if ( isLiveStream ) {
				playStateText = 'Streaming:'
			} else {
				playStateText = 'Loading:'
			}
		}

		if ( isPlaying && ! showLoading && ! mediaError ) {
			wrapClassName += ' is-playing'
		}

		if ( mediaError ) {
			if ( isLiveStream ) {
				playStateText = 'Livestream currently unavailable.'
			} else {
				playStateText = 'Failed to load:'
			}
		}

		let likeKey = ''
		let isLiked = false
		let isDisliked = false
		let likeButtonsClass = '-like-buttons is-active'
		let likeButtonClass = '-like'

		if ( nowPlaying && nowPlaying.audio_file_url ) {
			likeKey = nowPlaying.audio_file_url
		}

		if ( clickedLike ) {
			isLiked = true
			likeButtonClass += ' is-clicked'
		}

		if ( accountData && accountData.likes && accountData.likes[ likeKey ] ) {
			isLiked = true
			likeButtonClass += ' is-active'
		}

		return (
			<div className={ wrapClassName }>
				{ showLikeNotice &&
					<SiteNotice
						text="Please log in or create an account to use this feature"
						cancelText="Cancel"
						handleCancel={ this.handleCloseLikeNotice }
					>
						<div className="-actions">
							<a href="/signin">Log in</a>
						</div>
					</SiteNotice>
				}

				{ showFirstLikeNotice &&
					<SiteNotice
						text="This program has been added to your favourites"
						cancelText="OK"
						handleCancel={ this.handleCloseLikeNotice }
					>
						<div className="-actions">
							<Link to="/favourites" onClick={ this.handleCloseLikeNotice }>View my favourites</Link>
						</div>
					</SiteNotice>
				}

				<div className="now-playing">
					<div className="-inner">
						{ showRewindWarning &&
							<div className="-note">
								You may only rewind up to { rewindMaxHours } hours.<br />
								Greater access coming soon.
							</div>
						}

						<div className="-controls">
							<i className="-previous icon-angle-double-left-light" aria-hidden="true" onClick={ this.handlePreviousClick }></i>

							<div className={ rewindClass }>
								<i className="icon-undo-regular" aria-hidden="true" onClick={ this.handleRewindClick }></i>

								<span className="-thirty">30</span>
							</div>

							<NowPlayingButton />

							<div className={ fastForwardClass }>
								<i className="icon-redo-regular" aria-hidden="true" onClick={ this.handleFastForwardClick }></i>

								<span className="-thirty">30</span>
							</div>

							<i className={ nextClass } aria-hidden="true" onClick={ this.handleNextClick }></i>

							<TrackBar />
						</div>

						{ showLikeButtons && nowPlaying.is_program &&
							<div className={ likeButtonsClass }>
								<div className={ likeButtonClass }>
									{ isLiked &&
										<i className="icon-heart" onClick={ this.handleLikeClick } />
									||
										<i className="icon-heart-o" onClick={ this.handleLikeClick } />
									}
								</div>

								<div className="-like">
									<i className="icon-share" onClick={ this.handleShareClick } />
								</div>
							</div>
						}

						{ ! showRewindWarning && nowPlaying &&
							<div className="-text">
								<h3>
									<div onClick={ this.handleTextClick }>
										{ nowPlaying.is_program &&
											<span className="-series-wrap">
												<span>{ playStateText }&nbsp;</span>

												<span className="-series" dangerouslySetInnerHTML={ { __html: nowPlaying.series } } />
											</span>
										}

										{ nowPlaying.is_program && nowPlaying.title != nowPlaying.series &&
											<span className="-title" dangerouslySetInnerHTML={ { __html: nowPlaying.title } } />
										}

										{ ! nowPlaying.is_program &&
											<span>
												<span className="-title">Ads and promos</span>
											</span>
										}

										{ nowPlaying.is_program && nowPlaying.guests && nowPlaying.guests.length > 0 &&
											<span className="-guests">
												{ nowPlaying.title && nowPlaying.title != nowPlaying.series &&
													<span>- </span>
												}

												<span dangerouslySetInnerHTML={ { __html: nowPlaying.guests } } />
											</span>
										}
									</div>
								</h3>
							</div>
						}
					</div>

					{ showMiniSchedule &&
						<MiniSchedule programClickPlayDirect={ this.props.programClickPlayDirect } />
					}
				</div>
			</div>
		)
	}
}

const mapStateToProps = ( state, ownProps ) => {
	return {
		isBuffering: state.schedule.isBuffering,
		isPlaying: state.schedule.isPlaying,
		isLive: state.schedule.isLive,
		mediaError: state.schedule.mediaError,
		canFastForward: state.schedule.canFastForward,
		canGoToNext: state.schedule.canGoToNext,
		showRewindWarning: state.schedule.showRewindWarning,
		nowPlaying: state.schedule.nowPlaying,
		nowPlayingLive: state.schedule.nowPlayingLive,
		siteId: state.schedule.siteId,
		rewindMaxHours: state.schedule.rewindMaxHours,
		programsById: state.programs.byId,
		accountData: state.site.accountData,
		loggedIn: state.site.loggedIn,
		isEmbedded: state.site.isEmbedded
	}
}

const mapDispatchToProps = ( dispatch, ownProps ) => {
	return {
		goToPreviousTrack() {
			dispatch( goToPreviousTrack() )
		},

		goToNextTrack() {
			dispatch( goToNextTrack() )
		},

		rewind() {
			dispatch( rewind() )
		},

		fastForward() {
			dispatch( fastForward() )
		},

		calculateSeekPercent() {
			dispatch( calculateSeekPercent() )
		},

		setSeeking( value ) {
			dispatch( setSeeking( value ) )
		},

		setSelectedItem( key ) {
			dispatch( setSelectedItem( key ) )
		},

		likeProgram( program ) {
			dispatch( likeProgram( program ) )
		},

		showProgramMenu( program, source = 'now-playing' ) {
			dispatch( showProgramMenu( program, source ) )
		}
	}
}

export default connect( mapStateToProps, mapDispatchToProps )( NowPlaying )
